import { type FC, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import type { UHMember, UserHouseholdData } from 'src/types/Certification';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import HouseholdCompositionToolbar from './HouseholdCompositionToolbar';
import ProfileHouseholdMemberCard from './ProfileHouseholdMemberCard';

interface ProfileHouseholdMembersListProps {
  household: UserHouseholdData;
  currentUserId?: number;
  canManage?: boolean;
  onAddMember: () => void;
  onEditMember: (member: UHMember) => void;
  onRemoveMember: (member: UHMember) => void;
}

const ROLE_SORT_ORDER: Record<UHMember['household_role'], number> = {
  hoh: 0,
  co_hoh: 1,
  spouse: 2,
  member: 3,
  helper: 4,
};

/**
 * Member list for a profile (cert-less) UserHousehold. Mirrors the cert
 * composition list's structure but renders UserHouseholdMember data.
 */
const ProfileHouseholdMembersList: FC<ProfileHouseholdMembersListProps> = ({
  household,
  currentUserId,
  canManage = true,
  onAddMember,
  onEditMember,
  onRemoveMember,
}) => {
  const members = useMemo(() => {
    const list = [...(household.members ?? [])];
    return list.sort(
      (a, b) =>
        ROLE_SORT_ORDER[a.household_role] - ROLE_SORT_ORDER[b.household_role]
    );
  }, [household.members]);

  return (
    <Box sx={fadeStyle}>
      <HouseholdCompositionToolbar
        onAddMember={onAddMember}
        canManage={canManage}
        memberCount={members.length}
      />
      <Stack gap={2}>
        {members.map((member) => (
          <ProfileHouseholdMemberCard
            key={member.user_household_member_id}
            member={member}
            currentUserId={currentUserId}
            canManage={canManage}
            onEdit={onEditMember}
            onRemove={onRemoveMember}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default ProfileHouseholdMembersList;
